import React, { useEffect, useState } from 'react';
import { Alert, App as AntdApp, Button, Card, Form, Input, Typography } from 'antd';
import { ArrowLeftOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import AuthBrandPanel from '../components/auth/AuthBrandPanel';
import { AUTH_PAGE_STYLES } from '../components/auth/authPageStyles';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { authAPI } from '../services/api';
import { safeErrorMessage } from '../utils/errorHandler';

const { Title, Text } = Typography;

const ForgotPassword: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { mode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string>('');
  const [logoFailed, setLogoFailed] = useState(false);
  const { slogan, siteName, userLoginLogoUrl } = useSiteBranding();

  useEffect(() => {
    setLogoFailed(false);
  }, [userLoginLogoUrl]);

  const handleSubmit = async (values: { email: string }) => {
    setLoading(true);
    try {
      const result = await authAPI.forgotPassword(values.email);
      setNotice(result.message || '如果该邮箱已注册，重置密码邮件已发送。');
      message.success(result.message || '如果该邮箱已注册，重置密码邮件已发送。');
    } catch (err: any) {
      message.error(safeErrorMessage(err, '重置邮件发送失败，请稍后重试。'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth-page ${mode === 'dark' ? 'is-dark' : ''}`}>
      <style>{AUTH_PAGE_STYLES}</style>

      <AuthBrandPanel
        description={slogan}
        siteName={siteName}
        logoUrl={userLoginLogoUrl}
        logoFailed={logoFailed}
        onLogoError={() => setLogoFailed(true)}
      />

      <div className="auth-page-right">
        <div className="auth-shell">
          <Card className="auth-card" bordered={false}>
            <div className="auth-topbar">
              <div className="auth-badge">
                <SafetyCertificateOutlined />
                <span>密码重置</span>
              </div>
              <Link to="/login" className="auth-back-link">
                <ArrowLeftOutlined style={{ marginRight: 6 }} />
                返回登录
              </Link>
            </div>

            <div className="auth-header">
              <Title className="auth-title">忘记密码</Title>
              <Text className="auth-subtitle">
                输入注册邮箱，系统将发送重置密码链接；未激活账号也可通过该链接一并完成激活。
              </Text>
            </div>

            {notice ? (
              <Alert type="success" message={notice} showIcon style={{ marginBottom: 20, borderRadius: 14 }} />
            ) : null}

            <Form onFinish={handleSubmit} size="large" className="auth-form" layout="vertical">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入注册邮箱' },
                  { type: 'email', message: '请输入有效邮箱' },
                ]}
              >
                <Input className="auth-input" prefix={<MailOutlined />} placeholder="注册邮箱" autoComplete="email" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button className="auth-submit" type="primary" htmlType="submit" loading={loading} block>
                  发送重置邮件
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
